import { ApiError, type ApiErrorData } from "../services/apiClient";

// Shared backend-error vocabulary. The Rails API returns failures in several
// shapes ({ error }, { message }, { errors: [...] }, { errors: { field: [...] } })
// so every surface normalizes through here instead of hand-rolling parsing.
// UI layers feed the result into the toast store (toastApiError) or inline
// alerts — never render raw error objects.

export type ApiErrorKind =
  | "validation"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "unauthorized"
  | "unknown";

export interface NormalizedApiError {
  kind: ApiErrorKind;
  status?: number;
  message: string;
  fields?: Record<string, string[]>;
}

const FALLBACK_MESSAGE = "Something went wrong. Please try again.";

const kindForStatus = (status?: number): ApiErrorKind => {
  switch (status) {
    case 400:
    case 422:
      return "validation";
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "notFound";
    case 409:
      return "conflict";
    default:
      return "unknown";
  }
};

const flattenFieldErrors = (
  errors: Record<string, string[]>
): Pick<NormalizedApiError, "message" | "fields"> => ({
  message: Object.entries(errors)
    .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
    .join("; "),
  fields: errors,
});

// Rails model errors render as a TOP-LEVEL hash ({ end_date: [...], ... }),
// not nested under `errors`. Detect that shape: every remaining value is an
// array (after ignoring the conventional error/message/errors/status keys).
const topLevelFieldErrors = (
  data: ApiErrorData
): Record<string, string[]> | null => {
  const entries = Object.entries(data).filter(
    ([key]) => !["error", "message", "errors", "status"].includes(key)
  );
  if (
    entries.length === 0 ||
    !entries.every(([, value]) => Array.isArray(value))
  ) {
    return null;
  }
  return Object.fromEntries(entries) as Record<string, string[]>;
};

export const normalizeApiError = (
  error: unknown,
  fallback: string = FALLBACK_MESSAGE
): NormalizedApiError => {
  if (error instanceof ApiError) {
    const { status, data } = error;
    const kind = kindForStatus(status);
    const rawErrors = data?.errors;
    if (rawErrors && typeof rawErrors === "object" && !Array.isArray(rawErrors)) {
      const { message, fields } = flattenFieldErrors(
        rawErrors as Record<string, string[]>
      );
      return { kind, status, message, fields };
    }
    if (Array.isArray(rawErrors) && rawErrors.length > 0) {
      return { kind, status, message: rawErrors.join(", ") };
    }
    const topLevel = data ? topLevelFieldErrors(data) : null;
    if (topLevel) {
      const { message, fields } = flattenFieldErrors(topLevel);
      return { kind, status, message, fields };
    }
    const message = data?.error || data?.message || error.message || fallback;
    return { kind, status, message: String(message) };
  }
  if (error instanceof Error && error.message) {
    return { kind: "unknown", message: error.message };
  }
  if (typeof error === "string" && error) {
    return { kind: "unknown", message: error };
  }
  return { kind: "unknown", message: fallback };
};
